import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import MobileNav from '@/components/MobileNav';
import { TrendingUp, TrendingDown, Plus, Trash2, RefreshCw, DollarSign, BarChart2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POPULAR_ASSETS = [
  { symbol: 'AAPL', name: 'Apple Inc.', type: 'stock' },
  { symbol: 'MSFT', name: 'Microsoft', type: 'stock' },
  { symbol: 'TSLA', name: 'Tesla', type: 'stock' },
  { symbol: 'NVDA', name: 'NVIDIA', type: 'stock' },
  { symbol: 'AMZN', name: 'Amazon', type: 'stock' },
  { symbol: 'BTC', name: 'Bitcoin', type: 'crypto' },
  { symbol: 'ETH', name: 'Ethereum', type: 'crypto' },
  { symbol: 'SOL', name: 'Solana', type: 'crypto' },
];

export default function TradingSimulator() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [prices, setPrices] = useState({});
  const [tab, setTab] = useState('portfolio'); // 'portfolio' | 'market'

  useEffect(() => {
    fetchPortfolio();
    fetchMarketPrices();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const res = await axios.get(`${API}/simulator/portfolio`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPortfolio(res.data);
    } catch (err) {
      console.error('Failed to fetch simulator portfolio:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketPrices = async () => {
    try {
      const res = await axios.get(`${API}/simulator/prices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrices(res.data);
    } catch (err) {
      console.error('Failed to fetch prices:', err);
    }
  };

  const handleBuy = async () => {
    if (!selectedAsset || !quantity || parseFloat(quantity) <= 0) {
      toast.error('Veuillez entrer une quantité valide');
      return;
    }
    setBuying(true);
    try {
      await axios.post(
        `${API}/simulator/buy`,
        {
          symbol: selectedAsset.symbol,
          name: selectedAsset.name,
          asset_type: selectedAsset.type,
          quantity: parseFloat(quantity),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Achat de ${quantity} ${selectedAsset.symbol} réussi !`);
      setShowBuyModal(false);
      setQuantity('');
      setSelectedAsset(null);
      fetchPortfolio();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur lors de l\'achat';
      toast.error(msg);
    } finally {
      setBuying(false);
    }
  };

  const handleSell = async (holdingId, symbol) => {
    try {
      await axios.post(
        `${API}/simulator/sell`,
        { holding_id: holdingId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${symbol} vendu !`);
      fetchPortfolio();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la vente');
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Réinitialiser votre portefeuille simulé ? Vous récupérerez 10 000 $')) return;
    try {
      await axios.post(`${API}/simulator/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Portefeuille réinitialisé avec 10 000 $');
      fetchPortfolio();
    } catch (err) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const getCostBasis = (h) => h.quantity * h.avg_buy_price;
  const getCurrentValue = (h) => h.quantity * (prices[h.symbol] || h.current_price || h.avg_buy_price);
  const getPnL = (h) => getCurrentValue(h) - getCostBasis(h);
  const getPnLPct = (h) => getCostBasis(h) > 0 ? (getPnL(h) / getCostBasis(h)) * 100 : 0;

  const totalPortfolioValue = portfolio
    ? portfolio.cash_balance + (portfolio.holdings || []).reduce((s, h) => s + getCurrentValue(h), 0)
    : 0;

  const totalPnL = portfolio
    ? totalPortfolioValue - portfolio.initial_balance
    : 0;

  const totalPnLPct = portfolio?.initial_balance > 0
    ? (totalPnL / portfolio.initial_balance) * 100
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#ff6b35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <MobileNav currentPage="simulator" />

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8 animate-slide-up">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <button onClick={() => navigate('/academie')} className="hover:text-[#ff6b35] transition-colors">Académie</button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Simulateur de Trading</span>
          </div>
          <h1 className="text-3xl font-bold mb-1">Simulateur de Trading <span className="gradient-text">Virtuel</span></h1>
          <p className="text-gray-400">Entraînez-vous sans risque avec 10 000 $ virtuels</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass rounded-2xl p-5">
            <div className="text-xs text-gray-400 mb-1">Valeur totale</div>
            <div className="text-xl font-bold text-white">${totalPortfolioValue.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}</div>
          </div>
          <div className="glass rounded-2xl p-5">
            <div className="text-xs text-gray-400 mb-1">Cash disponible</div>
            <div className="text-xl font-bold text-green-400">${(portfolio?.cash_balance || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}</div>
          </div>
          <div className="glass rounded-2xl p-5">
            <div className="text-xs text-gray-400 mb-1">Gain / Perte</div>
            <div className={`text-xl font-bold ${totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {totalPnL >= 0 ? '+' : ''}${totalPnL.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
            </div>
          </div>
          <div className="glass rounded-2xl p-5">
            <div className="text-xs text-gray-400 mb-1">Performance</div>
            <div className={`text-xl font-bold ${totalPnLPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {totalPnLPct >= 0 ? '+' : ''}{totalPnLPct.toFixed(2)}%
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            data-testid="tab-portfolio"
            onClick={() => setTab('portfolio')}
            className={`px-5 py-2 rounded-xl font-semibold text-sm transition-all ${tab === 'portfolio' ? 'bg-[#ff6b35] text-[#0a0a0a]' : 'text-gray-400 hover:text-white glass'}`}
          >
            Mon Portefeuille ({portfolio?.holdings?.length || 0})
          </button>
          <button
            data-testid="tab-market"
            onClick={() => setTab('market')}
            className={`px-5 py-2 rounded-xl font-semibold text-sm transition-all ${tab === 'market' ? 'bg-[#ff6b35] text-[#0a0a0a]' : 'text-gray-400 hover:text-white glass'}`}
          >
            Marché
          </button>
        </div>

        {tab === 'portfolio' && (
          <div>
            {portfolio?.holdings?.length > 0 ? (
              <div className="space-y-3">
                {portfolio.holdings.map((h, idx) => {
                  const pnl = getPnL(h);
                  const pnlPct = getPnLPct(h);
                  const currentVal = getCurrentValue(h);
                  return (
                    <div key={h.id || idx} data-testid={`holding-${h.symbol}`} className="glass rounded-xl p-5">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-[#ff6b35]/10 flex items-center justify-center">
                            <span className="font-bold text-[#ff6b35] text-sm">{h.symbol.substring(0, 2)}</span>
                          </div>
                          <div>
                            <div className="font-bold text-white">{h.symbol}</div>
                            <div className="text-sm text-gray-400">{h.name}</div>
                          </div>
                        </div>

                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <div className="font-semibold text-white">${currentVal.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}</div>
                            <div className="text-sm text-gray-400">{h.quantity} × ${(prices[h.symbol] || h.current_price || h.avg_buy_price).toFixed(2)}</div>
                          </div>
                          <div className={`text-right ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            <div className="font-bold">{pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}</div>
                            <div className="text-sm">{pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%</div>
                          </div>
                          <Button
                            data-testid={`sell-btn-${h.symbol}`}
                            onClick={() => handleSell(h.id, h.symbol)}
                            variant="outline"
                            size="sm"
                            className="border-red-400/30 text-red-400 hover:bg-red-400/10"
                          >
                            Vendre
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="glass rounded-2xl p-12 text-center">
                <BarChart2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-300 mb-2">Portefeuille vide</h3>
                <p className="text-gray-500 mb-6">Allez sur le marché pour acheter vos premiers actifs virtuels</p>
                <Button
                  data-testid="go-to-market-btn"
                  onClick={() => setTab('market')}
                  className="bg-[#ff6b35] hover:bg-[#ff8555] text-[#0a0a0a] font-bold"
                >
                  Voir le Marché →
                </Button>
              </div>
            )}

            {/* Reset Button */}
            <div className="mt-6 flex justify-end">
              <Button
                data-testid="reset-portfolio-btn"
                onClick={handleReset}
                variant="outline"
                className="border-gray-700 text-gray-400 hover:text-red-400 hover:border-red-400/30"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Réinitialiser (10 000 $)
              </Button>
            </div>
          </div>
        )}

        {tab === 'market' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg">Actifs disponibles</h2>
              <Button
                onClick={fetchMarketPrices}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-[#ff6b35]"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Actualiser
              </Button>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              {POPULAR_ASSETS.map(asset => {
                const price = prices[asset.symbol];
                return (
                  <div key={asset.symbol} data-testid={`market-${asset.symbol}`} className="glass rounded-xl p-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#ff6b35]/10 flex items-center justify-center">
                        <span className="font-bold text-[#ff6b35] text-sm">{asset.symbol.substring(0, 2)}</span>
                      </div>
                      <div>
                        <div className="font-bold text-white">{asset.symbol}</div>
                        <div className="text-sm text-gray-400">{asset.name}</div>
                        <div className="text-xs text-gray-500 capitalize">{asset.type}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-semibold text-white">
                          {price ? `$${price.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}` : '—'}
                        </div>
                      </div>
                      <Button
                        data-testid={`buy-btn-${asset.symbol}`}
                        onClick={() => { setSelectedAsset(asset); setShowBuyModal(true); }}
                        size="sm"
                        className="bg-[#ff6b35] hover:bg-[#ff8555] text-[#0a0a0a] font-semibold"
                      >
                        Acheter
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Buy Modal */}
        {showBuyModal && selectedAsset && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
            <div className="glass rounded-2xl p-8 max-w-md w-full border border-[#ff6b35]/20">
              <h3 className="text-xl font-bold mb-2">Acheter {selectedAsset.symbol}</h3>
              <p className="text-gray-400 text-sm mb-1">{selectedAsset.name}</p>
              {prices[selectedAsset.symbol] && (
                <p className="text-[#ff6b35] font-semibold mb-6">
                  Prix actuel : ${prices[selectedAsset.symbol]?.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
                </p>
              )}

              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">Quantité</label>
                <Input
                  data-testid="buy-quantity-input"
                  type="number"
                  min="0.00001"
                  step="0.00001"
                  value={quantity}
                  onChange={e => setQuantity(e.target.value)}
                  placeholder="Ex: 0.5"
                  className="bg-gray-900 border-gray-700 text-white"
                />
                {quantity && prices[selectedAsset.symbol] && (
                  <p className="text-sm text-gray-400 mt-2">
                    Total : ${(parseFloat(quantity) * prices[selectedAsset.symbol]).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
                    &nbsp;(Cash dispo : ${(portfolio?.cash_balance || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })})
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => { setShowBuyModal(false); setQuantity(''); setSelectedAsset(null); }}
                  variant="outline"
                  className="flex-1 border-gray-700 text-gray-400"
                >
                  Annuler
                </Button>
                <Button
                  data-testid="confirm-buy-btn"
                  onClick={handleBuy}
                  disabled={buying || !quantity}
                  className="flex-1 bg-[#ff6b35] hover:bg-[#ff8555] text-[#0a0a0a] font-bold"
                >
                  {buying ? 'Achat...' : `Confirmer l'achat`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
